import React, { useEffect } from 'react';
import { useI18n } from '../i18n/i18nContext';
import Hero from '../components/Hero';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useServiceStore } from '../stores/serviceStore';
import LocalizedLink from '../components/LocalizedLink';
import { getTranslatedField } from '../utils/translationUtils';
import NewsletterSubscription from '../components/NewsletterSubscription';
import ContentPageRenderer from '../components/ContentPageRenderer';
import SEO from '../components/SEO';
import CarouselGallery from '../components/CarouselGallery';

const PackagesPage: React.FC = () => {
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.home);
  const { services, loadServices } = useServiceStore();

  useEffect(() => {
    loadServices(language);
  }, [loadServices, language]);

  const contrastServiceSlug = 'contrast-therapy';
  const mainServices = services.filter(s => s.slug !== contrastServiceSlug);
  const contrastService = services.find(s => s.slug === contrastServiceSlug);

  return (
    <div className="flex flex-col">
      <SEO description={t.seo.homeDescription} />
      <Hero />

      <ContentPageRenderer 
        slug="home-intro" 
        className="container mx-auto px-4 mt-8 max-w-6xl" 
      />

      <main className="flex-grow">
        <div id="services" className="container mx-auto px-4 py-16 max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-8">{t.ourServices}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainServices.map((service) => (
              <LocalizedLink
                to={`/services/${service.slug || ''}`}
                key={service.id}
                className="block bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105"
              >
                <img
                  className="w-full h-48 object-cover"
                  src={service.image || `/images/services/${service.slug}.webp`}
                  alt={getTranslatedField(service, 'name', language)}
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{getTranslatedField(service, 'name', language)}</h3>
                  <p className="text-gray-600 mb-4">{getTranslatedField(service, 'description', language)}</p>
                  <span className="hidden md:inline text-blue-600 font-semibold hover:underline text-sm">
                    {t.learnMore} &rarr;
                  </span>
                </div>
              </LocalizedLink>
            ))}
          </div>

          {contrastService && (
            <LocalizedLink
              to={`/services/${contrastService.slug || ''}`}
              key={contrastService.id}
              className="mt-8 block bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105"
            >
              <div className="md:flex md:items-stretch">
                <div className="md:w-48 md:flex-shrink-0">
                  <img
                    className="h-48 w-full object-cover"
                    src={contrastService.image || '/images/services/contrast-therapy.webp'}
                    alt={getTranslatedField(contrastService, 'name', language)}
                  />
                </div>
                <div className="p-6 md:flex-1 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold mb-2">{getTranslatedField(contrastService, 'name', language)}</h3>
                  <p className="text-gray-700 mb-4">{getTranslatedField(contrastService, 'description', language)}</p>
                  <span className="hidden md:inline text-blue-600 font-semibold hover:underline text-sm">
                    {t.learnMore} &rarr;
                  </span>
                </div>
              </div>
            </LocalizedLink>
          )}
        </div>
      </main>

      <CarouselGallery />
      <NewsletterSubscription />
    </div>
  );
};

export default PackagesPage;
